import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Field, useForm } from 'react-final-form';

import colors from '@front/common/src/utils/colors';
import useBoolean from '@front/common/src/hooks/use-boolean';
import useDebounce from '@front/common/src/hooks/use-debounce';

import { translate } from '@front/i18n';

import InputRange, { ANIMATION_DURATION } from '@front/web-ui/src/components/input-range/input-range';
import Text from '@front/web-ui/src/components/base-text/variants/text';

import { useAnimatedIncrementedValue } from '../../hooks/use-animated-incremented-value';

const t = translate('screens.revenue_simulator');

const StyledDisponibilityLabel = styled.label`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const StyledInputRange = styled(InputRange)`
  :focus-visible {
    outline: 2px auto ${colors.ink50};
  }
`;

type DisponibilitySliderProps = {
  id: string;
  name: string;
  min?: number;
  max: number;
  step?: number;
  hours: string;
  initialValue?: number;
};

type AnimatedHoursTextProps = {
  hours: number;
};

const AnimatedHoursText = ({ hours }: AnimatedHoursTextProps) => {
  const count = useAnimatedIncrementedValue({ from: 1, to: hours });

  return <Text.MDMedium>{t('.number_of_hours', { count })}</Text.MDMedium>;
};

const DisponibilitySlider = React.forwardRef<HTMLInputElement, DisponibilitySliderProps>(
  ({ id, name, min = 0, max, step = 1, hours, initialValue = 1 }, ref) => {
    const form = useForm();
    const isSafari = navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome');

    const { value, setFalse: disableTransition } = useBoolean(true);
    const hasTransition = useDebounce<boolean>(value, ANIMATION_DURATION);

    // Here we are disabling animation on safari after the first animation duration (300ms)
    // This is due to existing glitch on safari if we let transition on sudo elements
    useEffect(() => {
      if (isSafari) disableTransition();
    }, [disableTransition, isSafari]);

    useEffect(() => {
      form.mutators.setValue(name, initialValue);
    }, [form.mutators, initialValue, name]);

    return (
      <>
        <StyledDisponibilityLabel htmlFor={id}>
          <Text.MDBold>{t('.available')}</Text.MDBold>
          <AnimatedHoursText hours={+hours} />
        </StyledDisponibilityLabel>
        <Field name={name}>
          {({ input }) => (
            <StyledInputRange
              {...input}
              id={id}
              name={name}
              min={min}
              max={max}
              step={step}
              ref={ref}
              hasTransition={hasTransition}
            />
          )}
        </Field>
      </>
    );
  },
);

export default DisponibilitySlider;
